import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
} from "@mui/material";
// import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { useState } from "react";

import { Row, Label, SwitchRow } from "../index.styles";
import { Select, ToggleButton } from "../../../components";

import { DURATION_OPTIONS } from "../../ProductAnalyzer/common/DurationFilter";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectProductAnalyzerSettings } from "../../../features/settings/settings.selector";
import { TDefaultChartTimeSpan } from "../../../features/settings/settings.types";
import { updateProductAnalyzerSettingsThunk } from "../../../features/settings/settings.thunk";
import IOSSwitch from "../../../components/IOSSwitch";

const DisplaySettings = () => {
  const dispatch = useAppDispatch();
  const productAnalyzerSettings = useAppSelector(selectProductAnalyzerSettings);
  const [isQuickInfoOpen, setIsQuickInfoOpen] = useState(true);
  const [isChartsOpen, setIsChartsOpen] = useState(true);
  const [isOtherTimeFramesOpen, setIsOtherTimeFramesOpen] = useState(true);

  if (!productAnalyzerSettings) return null;

  return (
    <Grid
      container
      spacing="16px"
      justifyContent="center"
      sx={{
        overflowX: "visible",
        flexWrap: "wrap",
        "@media (max-width:319px)": {
          overflowX: "auto",
          flexWrap: "nowrap",
          justifyContent: "flex-start",
        },
      }}
    >
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          minWidth: "auto",
          maxWidth:"560px",
          "@media (max-width:319px)": {
            minWidth: "320px",
          },
        }}
      >
        <Stack spacing={1}>
          <Card
            sx={{
              boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
              border: "1px solid #D4D6D8",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 8px",
                minHeight: "30px !important",
                height: "30px",
                cursor: "pointer",
                backgroundColor: "#F0F0F0",
                borderBottom: "1px solid #C9C9C9",
                borderRadius: "4px 4px 0px 0px",
              }}
              onClick={() => setIsQuickInfoOpen(!isQuickInfoOpen)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <Box
                  sx={{
                    width: 25,
                    height: 25,
                    borderRadius: "14px",
                    backgroundColor: "#3366FF1A",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      fontSize: 16,
                      color: "#0F56CA",
                    }}
                  />
                </Box> */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                >
                  Quick Info
                </Typography>
              </Box>
              <IconButton
                size="small"
                sx={{ padding: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsQuickInfoOpen(!isQuickInfoOpen);
                }}
              >
                {isQuickInfoOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Box>
            <Collapse in={isQuickInfoOpen}>
              <CardContent
                sx={{
                  padding: "0px",
                  "&:last-child": { paddingBottom: "0px" },
                }}
              >
                <Stack spacing={0}>
                  <SwitchRow>
                    <Label tooltip="Displays estimated profit per unit.">
                      Show Profit
                    </Label>
                    <IOSSwitch
                      checked={productAnalyzerSettings.showProfitInQuickInfo}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showProfitInQuickInfo:
                              !productAnalyzerSettings.showProfitInQuickInfo,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <SwitchRow>
                    <Label tooltip="Shows profit as a percentage of selling price.">
                      Show Profit Margin
                    </Label>

                    <IOSSwitch
                      checked={
                        productAnalyzerSettings.showProfitMarginInQuickInfo
                      }
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showProfitMarginInQuickInfo:
                              !productAnalyzerSettings.showProfitMarginInQuickInfo,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <SwitchRow>
                    <Label tooltip="Displays return on investment based on total costs.">
                      Show ROI
                    </Label>

                    <IOSSwitch
                      checked={productAnalyzerSettings.showRoiInQuickInfo}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showRoiInQuickInfo:
                              !productAnalyzerSettings.showRoiInQuickInfo,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <SwitchRow>
                    <Label tooltip="Shows the minimum price needed to avoid a loss.">
                      Show Breakeven
                    </Label>

                    <IOSSwitch
                      checked={productAnalyzerSettings.showBreakevenInQuickInfo}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showBreakevenInQuickInfo:
                              !productAnalyzerSettings.showBreakevenInQuickInfo,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <SwitchRow>
                    <Label tooltip="Displays a quick overview of competing sellers and offers.">
                      Show Offer Summary
                    </Label>

                    <IOSSwitch
                      checked={
                        productAnalyzerSettings.showOfferSummaryInQuickInfo
                      }
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showOfferSummaryInQuickInfo:
                              !productAnalyzerSettings.showOfferSummaryInQuickInfo,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <Box
                    padding="4px"
                    display="flex"
                    gap="4px"
                    flexDirection="column"
                  >
                    <Box display="flex" justifyContent="space-between">
                      <Label tooltip="Shows average pricing over the selected time period.">
                        Show Average Pricing
                      </Label>

                      <IOSSwitch
                        checked={
                          productAnalyzerSettings.showAveragePricingInQuickInfo
                        }
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) =>
                          dispatch(
                            updateProductAnalyzerSettingsThunk({
                              showAveragePricingInQuickInfo:
                                !productAnalyzerSettings.showAveragePricingInQuickInfo,
                            }),
                          )
                        }
                      />
                    </Box>

                    <Box display="flex" gap="4px" justifyContent="center">
                      {DURATION_OPTIONS.map((option) => (
                        <ToggleButton
                          key={`fba_avg_${option.id}`}
                          value={option.id}
                          selected={
                            productAnalyzerSettings.defaultAverageTimeFrameInQuickInfo ===
                            option.id
                          }
                          onClick={() =>
                            dispatch(
                              updateProductAnalyzerSettingsThunk({
                                defaultAverageTimeFrameInQuickInfo: option.id,
                              }),
                            )
                          }
                          sx={{ width: "60px" }}
                        >
                          {option.label}
                        </ToggleButton>
                      ))}
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Collapse>
          </Card>

          <Card
            sx={{
              boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
              border: "1px solid #D4D6D8",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 8px",
                minHeight: "30px !important",
                height: "30px",
                cursor: "pointer",
                backgroundColor: "#F0F0F0",
                borderBottom: "1px solid #C9C9C9",
                borderRadius: "4px 4px 0px 0px",
              }}
              onClick={() => setIsChartsOpen(!isChartsOpen)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <Box
                  sx={{
                    width: 25,
                    height: 25,
                    borderRadius: "14px",
                    backgroundColor: "#3366FF1A",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      fontSize: 16,
                      color: "#0F56CA",
                    }}
                  />
                </Box> */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                >
                  Charts
                </Typography>
              </Box>
              <IconButton
                size="small"
                sx={{ padding: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsChartsOpen(!isChartsOpen);
                }}
              >
                {isChartsOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Box>
            <Collapse in={isChartsOpen}>
              <CardContent sx={{ padding: "0 !important" }}>
                <Stack spacing={0}>
                  <SwitchRow>
                    <Label tooltip="Displays historical price trends.">
                      Show Prices Chart
                    </Label>

                    <IOSSwitch
                      checked={productAnalyzerSettings.showPricesChart}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showPricesChart:
                              !productAnalyzerSettings.showPricesChart,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <SwitchRow>
                    <Label tooltip="Show the Monthly Sold chart in the Charts panel.">
                      Show Sold Chart
                    </Label>

                    <IOSSwitch
                      checked={productAnalyzerSettings.showSoldChart}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showSoldChart:
                              !productAnalyzerSettings.showSoldChart,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <SwitchRow>
                    <Label tooltip="Show the Offers, Reviews and Ratings chart in the Charts panel.">
                      Show Offers Chart
                    </Label>

                    <IOSSwitch
                      checked={productAnalyzerSettings.showOffersChart}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            showOffersChart:
                              !productAnalyzerSettings.showOffersChart,
                          }),
                        )
                      }
                    />
                  </SwitchRow>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <Row>
                    <Label tooltip="Limit the history shown on the charts. Shorter periods improve performance.">
                      Chart Time Span
                    </Label>
                    <Select
                      value={
                        productAnalyzerSettings.defaultChartTimeSpan ||
                        "OneMonth"
                      }
                      onChange={(e) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            defaultChartTimeSpan: e.target
                              .value as TDefaultChartTimeSpan,
                          }),
                        )
                      }
                      size="medium"
                      fullWidth
                    >
                      <MenuItem value={"OneDay"}>One day</MenuItem>
                      <MenuItem value={"OneWeek"}>One week</MenuItem>
                      <MenuItem value={"OneMonth"}>One month</MenuItem>
                      <MenuItem value={"ThreeMonths"}>Three months</MenuItem>
                      <MenuItem value={"OneYear"}>One year</MenuItem>
                    </Select>
                  </Row>
                </Stack>
              </CardContent>
            </Collapse>
          </Card>

          <Card
            sx={{
              boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
              borderRadius: "4px",
              border: "1px solid #D4D6D8",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 8px",
                minHeight: "30px !important",
                height: "30px",
                cursor: "pointer",
                backgroundColor: "#F0F0F0",
                borderBottom: "1px solid #C9C9C9",
                borderRadius: "4px 4px 0px 0px",
              }}
              onClick={() => setIsOtherTimeFramesOpen(!isOtherTimeFramesOpen)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* <Box
                  sx={{
                    width: 25,
                    height: 25,
                    borderRadius: "14px",
                    backgroundColor: "#3366FF1A",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TrendingUpIcon
                    sx={{
                      fontSize: 16,
                      color: "#0F56CA",
                    }}
                  />
                </Box> */}
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                >
                  Other Time Frames
                </Typography>
              </Box>
              <IconButton
                size="small"
                sx={{ padding: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOtherTimeFramesOpen(!isOtherTimeFramesOpen);
                }}
              >
                {isOtherTimeFramesOpen ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Box>
            <Collapse in={isOtherTimeFramesOpen}>
              <CardContent
                sx={{
                  padding: "0 !important",
                }}
              >
                <Stack spacing={0}>
                  <Row>
                    <Label tooltip="The default time frame for the ranks and prices panel. Select either current or an average over a specified period.">
                      Ranks & Prices
                    </Label>
                    <Select
                      value={
                        productAnalyzerSettings.defaultRanksAndPricesTimeFrame
                      }
                      onChange={(e) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            defaultRanksAndPricesTimeFrame: e.target
                              .value as string,
                          }),
                        )
                      }
                      size="medium"
                      fullWidth
                    >
                      {DURATION_OPTIONS.map((option) => (
                        <MenuItem
                          key={`ranks_and_prices_time_frame_${option.id}`}
                          value={option.id}
                        >
                          {option.id === "Current"
                            ? "Current"
                            : option.id === "All"
                              ? "All"
                              : `${option.days} Days`}
                        </MenuItem>
                      ))}
                    </Select>
                  </Row>

                  <Divider sx={{ borderColor: "#E5E7EB", my: 0 }} />

                  <Row>
                    <Label tooltip="The default time frame for the buy box analysis panel.">
                      Buy Box Analysis
                    </Label>
                    <Select
                      value={productAnalyzerSettings.defaultBbAnalysisTimeFrame}
                      onChange={(e) =>
                        dispatch(
                          updateProductAnalyzerSettingsThunk({
                            defaultBbAnalysisTimeFrame: e.target
                              .value as string,
                          }),
                        )
                      }
                      size="medium"
                      fullWidth
                    >
                      {DURATION_OPTIONS.filter((option) => !!option.range).map(
                        (option) => (
                          <MenuItem
                            key={`bb_analysis_time_frame_${option.id}`}
                            value={option.id}
                          >
                            {option.id === "All"
                              ? "All"
                              : `${option.days} Days`}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </Row>
                </Stack>
              </CardContent>
            </Collapse>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default DisplaySettings;
